import React from "react";
import { Form, Button, Table, Modal, Spinner } from "react-bootstrap";
import { PlusSquare } from "react-bootstrap-icons";
import AuthorizedPage from "../../component/AuthorizedPage";
import WarehouseApi from "../../api/WarehouseApi";
import { Role } from "../../domain/Role";
import GoogleMapPicker from "../../component/GoogleMapPicker";
import { updateState } from "../StateHelper";
import { OZISIK_WAREHOUSE_LOCATION } from '../../Constants';

export enum WarehouseType {
  COLD_STORAGE = "COLD_STORAGE",
  COLD_STORAGE_MINUS_18 = "COLD_STORAGE_MINUS_18",
  SOLID_STORAGE = "SOLID_STORAGE",
  LIQUID_STORAGE = "LIQUID_STORAGE",
  MATERIAL_STORAGE = "MATERIAL_STORAGE"
}

export const WarehouseTypeLabels: Record<WarehouseType, string> = {
  [WarehouseType.COLD_STORAGE]: "Soğuk Hava Deposu",
  [WarehouseType.COLD_STORAGE_MINUS_18]: "Soğuk Hava Deposu(-18°C)",
  [WarehouseType.SOLID_STORAGE]: "Katı Depolama",
  [WarehouseType.LIQUID_STORAGE]: "Sıvı Depolama",
  [WarehouseType.MATERIAL_STORAGE]: "Malzeme Deposu",
};

interface WarehouseDto {
  id?: number;
  name?: string;
  shortName?: string;
  description?: string;
  address?: string;
  phone?: string;
  capacityInM3?: number;
  warehouseType?: WarehouseType;
  surfaceAreaInSquareMeters?: number;
  estimatedOccupancyRate?: number;
  latitude?: number;
  longitude?: number;
}

interface WarehouseManagementState {
  warehouses: WarehouseDto[];
  showWarehouseCreateModal: boolean;
  selectedLat: number;
  selectedLng: number;
  isSubmitting: boolean;
}

const initialState: WarehouseManagementState = {
  warehouses: [],
  showWarehouseCreateModal: false,
  selectedLat: OZISIK_WAREHOUSE_LOCATION.latitude,
  selectedLng: OZISIK_WAREHOUSE_LOCATION.longitude,
  isSubmitting: false,
};

class WarehouseManagement extends React.Component<{}, WarehouseManagementState> {
  constructor(props: {}) {
    super(props);
    this.state = initialState;
    this.onSubmit = this.onSubmit.bind(this);
  }

  componentDidMount() {
    this.loadWarehouses();
  }

  async loadWarehouses() {
    const response = await WarehouseApi.listAllWarehouses();
    if (response.success) {
      updateState(this, "warehouses", response.data);
    }
  }

  onCreateNewWarehouse = () => {
    updateState(this, "showWarehouseCreateModal", true);
  };

  onModalClose = () => {
    updateState(this, "showWarehouseCreateModal", false);
  };

  onLocationSelect = (lat: number, lng: number) => {
    this.setState({ selectedLat: lat, selectedLng: lng });
  };

  async onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (this.state.isSubmitting) return;

    this.setState({ isSubmitting: true });

    const form = e.currentTarget;
    const formData = new FormData(form);
    const formObject: any = Object.fromEntries(formData.entries());

    formObject.capacityInM3 = parseFloat(formObject.capacityInM3) || 0;
    formObject.surfaceAreaInSquareMeters = parseFloat(formObject.surfaceAreaInSquareMeters) || 1.0;
    formObject.warehouseType = formObject.warehouseType || WarehouseType.COLD_STORAGE;
    formObject.latitude = this.state.selectedLat;
    formObject.longitude = this.state.selectedLng;

    try {
      const response = await WarehouseApi.createWarehouse(formObject);
      if (response.success) {
        form.reset();
        await this.loadWarehouses();
        this.onModalClose();
      } else {
        console.error("Depo oluşturma başarısız:", response.error);
      }
    } finally {
      this.setState({ isSubmitting: false });
    }
  }

  renderWarehousesTable() {
    return (
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Ad</th>
            <th>Kısa Ad</th>
            <th>Açıklama</th>
            <th>Adres</th>
            <th>Telefon</th>
            <th>Kapasite (m³)</th>
            <th>Depo Türü</th>
            <th>Yüzey Alanı (m²)</th>
            <th>Doluluk (%)</th>
          </tr>
        </thead>
        <tbody>
          {this.state.warehouses.map((w) => (
            <tr key={w.id}>
              <td>{w.id ?? ""}</td>
              <td>{w.name ?? ""}</td>
              <td>{w.shortName ?? ""}</td>
              <td>{w.description ?? ""}</td>
              <td>{w.address ?? ""}</td>
              <td>{w.phone ?? ""}</td>
              <td>{w.capacityInM3 ?? ""}</td>
              <td>{w.warehouseType ? WarehouseTypeLabels[w.warehouseType] : ""}</td>
              <td>{w.surfaceAreaInSquareMeters ?? ""}</td>
              <td>{w.estimatedOccupancyRate ?? 0}%</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderWarehouseCreateModal() {
    return (
      <Modal
        show={this.state.showWarehouseCreateModal}
        onHide={this.onModalClose}
        dialogClassName="modal-70w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Depo Oluştur</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={this.onSubmit}>

            <Form.Group className="mb-3">
              <Form.Label>Kısa Ad</Form.Label>
              <Form.Control name="shortName" type="text" placeholder="Kısa Ad" required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Açıklama</Form.Label>
              <Form.Control name="description" type="text" placeholder="Açıklama" />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Adres</Form.Label>
              <Form.Control name="address" type="text" placeholder="Adres" />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Telefon</Form.Label>
              <Form.Control name="phone" type="text" placeholder="Telefon" />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kapasite (m³)</Form.Label>
              <Form.Control name="capacityInM3" type="number" min={0} placeholder="Kapasite" />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Depo Türü</Form.Label>
              <Form.Select name="warehouseType" defaultValue={WarehouseType.COLD_STORAGE}>
                {Object.values(WarehouseType).map((type) => (
                  <option key={type} value={type}>
                    {WarehouseTypeLabels[type]}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Yüzey Alanı (m²)</Form.Label>
              <Form.Control name="surfaceAreaInSquareMeters" type="number" step="0.1" min={1} placeholder="Yüzey alanı" />
            </Form.Group>

            <Form.Label>Konum Seçimi</Form.Label>
            <GoogleMapPicker
              latitude={this.state.selectedLat}
              longitude={this.state.selectedLng}
              onSelectLocation={this.onLocationSelect}
            />

            <Form.Control type="hidden" name="latitude" value={this.state.selectedLat} />
            <Form.Control type="hidden" name="longitude" value={this.state.selectedLng} />

            <Button
              variant="primary"
              type="submit"
              className="mt-3 float-end"
              disabled={this.state.isSubmitting}
            >
              {this.state.isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  /> Oluşturuluyor...
                </>
              ) : (
                "Oluştur"
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    );
  }

  render() {
    return (
      <AuthorizedPage role={Role.WAREHOUSE_ADMIN}>
        <div className="row mb-3">
          <div className="col-10"></div>
          <div className="col-2">
            <Button
              variant="success"
              style={{ float: "right" }}
              onClick={this.onCreateNewWarehouse}
            >
              <PlusSquare size={18} /> Yeni Depo Oluştur
            </Button>
          </div>
        </div>

        {this.renderWarehousesTable()}
        {this.renderWarehouseCreateModal()}
      </AuthorizedPage>
    );
  }
}

export default WarehouseManagement;
